import React from "react";
import { InputNumber, Select, Button, Switch, Divider, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import NumatoFlatMessageFields from "./NumatoFlatMessageFields";
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
import {
  type NumatoFlatMessage,
  buildDefaultNumatoFlatMessage,
  readNumatoFlatMessage,
} from "../../../flatbuffers/types/numatoFlatMessage";

// ************************************************ */
// TYPES ****************************************** */
interface BankOrLiftId {
  id: string;
  name: string;
}

interface SetValueContent {
  kind: "SetValueMessage";
  chip: number;
  line: number;
  direction: number;
  value: boolean;
  name: string;
}

interface GetValueContent {
  kind: "GetValueMessage";
  chip: number;
  line: number;
  name: string;
  value: boolean;
  event_type: number;
}

type GpioCommandContent = SetValueContent | GetValueContent;

interface GpioCommand {
  kind: "GpioCommandContentTable";
  content: GpioCommandContent;
}

interface MsgToCmdEntry {
  /**
   * The LN2 message, as the flat quad LiftCloud's STM32 save path decodes
   * (`encoded_type`, `message_content`, `message_value` plus the device branch,
   * position and `active`) — the same contract the Numato editors use.
   */
  key: NumatoFlatMessage;
  value: GpioCommand;
}

interface CmdToLiftEntry {
  key: GpioCommand;
  value: BankOrLiftId;
}

interface Stm32CdoState {
  board_name: string;
  board_address: string;
  dev_type: number;
  bank_id: BankOrLiftId;
  msg_to_cmd: MsgToCmdEntry[];
  stm_cmd_to_lift_id: CmdToLiftEntry[];
}

// ************************************************ */
// CONSTANTS ************************************** */
const DEV_TYPES = Object.entries(DevType)
  .filter(([, v]) => typeof v === "number")
  .map(([label, value]) => ({ label: `${label} (${value})`, value: value as number }));

const GPIO_CMD_TYPES = [
  { label: "SetValueMessage", value: "SetValueMessage" },
  { label: "GetValueMessage", value: "GetValueMessage" },
];

const DIRECTION_OPTIONS = [
  { label: "Input (0)", value: 0 },
  { label: "Output (1)", value: 1 },
];

const EVENT_TYPE_OPTIONS = [
  { label: "RisingEdge (0)", value: 0 },
  { label: "FallingEdge (1)", value: 1 },
  { label: "BothEdges (2)", value: 2 },
];

// ************************************************ */
// DEFAULTS *************************************** */
function buildDefaultSetValue(): SetValueContent {
  return {
    kind: "SetValueMessage",
    chip: 0,
    line: 0,
    direction: 1,
    value: true,
    name: "",
  };
}

function buildDefaultGetValue(): GetValueContent {
  return {
    kind: "GetValueMessage",
    chip: 0,
    line: 0,
    name: "",
    value: false,
    event_type: 0,
  };
}

function buildDefaultGpioCommand(): GpioCommand {
  return {
    kind: "GpioCommandContentTable",
    content: buildDefaultSetValue(),
  };
}

function buildDefaultState(): Stm32CdoState {
  return {
    board_name: "",
    board_address: "0x20",
    dev_type: 1,
    bank_id: { id: "", name: "" },
    msg_to_cmd: [],
    stm_cmd_to_lift_id: [],
  };
}

// ************************************************ */
// NORMALISERS ************************************ */
// Everything the server (or an older save) hands back is untrusted: a field
// can be missing, null, or the wrong type. These coerce each piece to its
// well-formed shape so a malformed payload can never throw during render.
function asRecord(raw: unknown): Record<string, unknown> {
  return raw !== null && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function asString(raw: unknown, fallback = ""): string {
  return typeof raw === "string" ? raw : fallback;
}

function asNumber(raw: unknown, fallback: number): number {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : fallback;
}

function asBoolean(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

/** A bank or lift reference: string `id` and `name` only, "" otherwise. */
function normaliseBankOrLiftId(raw: unknown): BankOrLiftId {
  const src = asRecord(raw);
  return { id: asString(src.id), name: asString(src.name) };
}

/**
 * A GPIO command in the editor's own shape. The inner `kind` selects the
 * variant; every field of that variant is present afterwards, defaulted from
 * the same builders a newly added row uses. Anything whose inner `kind` is not
 * a known variant becomes a fresh default command.
 */
function normaliseGpioCommand(raw: unknown): GpioCommand {
  const content = asRecord(asRecord(raw).content);

  if (content.kind === "SetValueMessage") {
    const d = buildDefaultSetValue();
    return {
      kind: "GpioCommandContentTable",
      content: {
        kind: "SetValueMessage",
        chip: asNumber(content.chip, d.chip),
        line: asNumber(content.line, d.line),
        direction: asNumber(content.direction, d.direction),
        value: asBoolean(content.value, d.value),
        name: asString(content.name, d.name),
      },
    };
  }

  if (content.kind === "GetValueMessage") {
    const d = buildDefaultGetValue();
    return {
      kind: "GpioCommandContentTable",
      content: {
        kind: "GetValueMessage",
        chip: asNumber(content.chip, d.chip),
        line: asNumber(content.line, d.line),
        name: asString(content.name, d.name),
        value: asBoolean(content.value, d.value),
        event_type: asNumber(content.event_type, d.event_type),
      },
    };
  }

  return buildDefaultGpioCommand();
}

function asEntries(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

/**
 * The bank this document belongs to. A bank assignment from the wizard fills
 * an empty id (the editor hides the bank inputs when it has one, so the user
 * cannot supply it), but never replaces an id that is already populated.
 */
function resolveBank(bank: BankOrLiftId, ctxBankId?: string, ctxBankName?: string): BankOrLiftId {
  if (ctxBankId === undefined || bank.id !== "") return bank;
  return { id: ctxBankId, name: ctxBankName ?? "" };
}

function normaliseState(raw: unknown, ctxBankId?: string, ctxBankName?: string): Stm32CdoState {
  const src = asRecord(raw);
  const defaults = buildDefaultState();
  return {
    board_name: asString(src.board_name, defaults.board_name),
    board_address: asString(src.board_address, defaults.board_address),
    dev_type: asNumber(src.dev_type, defaults.dev_type),
    bank_id: resolveBank(normaliseBankOrLiftId(src.bank_id), ctxBankId, ctxBankName),
    msg_to_cmd: asEntries(src.msg_to_cmd).map((entry) => {
      const e = asRecord(entry);
      return { key: readNumatoFlatMessage(e.key), value: normaliseGpioCommand(e.value) };
    }),
    stm_cmd_to_lift_id: asEntries(src.stm_cmd_to_lift_id).map((entry) => {
      const e = asRecord(entry);
      return { key: normaliseGpioCommand(e.key), value: normaliseBankOrLiftId(e.value) };
    }),
  };
}

// ************************************************ */
// ENTRY CARD STYLE ******************************* */
const entryStyle: React.CSSProperties = {
  border: "1px solid #303030",
  borderRadius: 6,
  padding: 12,
  marginBottom: 12,
  position: "relative",
};

const deleteButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: 8,
  right: 8,
};

// ************************************************ */
// GPIO COMMAND FIELDS RENDERER ******************* */
function renderGpioCommandFields(cmd: GpioCommand, onChange: (updated: GpioCommand) => void) {
  const { content } = cmd;
  return (
    <>
      <div className="row">
        <div className="col-12 col-md-4 mt-2">
          <span className="generalStyles__inputLabel">Command Type</span>
          <Select
            className="generalStyles__width100"
            size="small"
            value={content.kind}
            onChange={(val: string) => {
              const next =
                val === "SetValueMessage" ? buildDefaultSetValue() : buildDefaultGetValue();
              onChange({ ...cmd, content: next });
            }}
            style={{ marginTop: 4 }}
          >
            {GPIO_CMD_TYPES.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="col-12 col-md-4 mt-2">
          <span className="generalStyles__inputLabel">Name</span>
          <Input
            size="small"
            value={content.name}
            onChange={(e) =>
              onChange({
                ...cmd,
                content: {
                  ...content,
                  name: e.target.value,
                },
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-6 col-md-2 mt-2">
          <span className="generalStyles__inputLabel">Chip</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            max={255}
            value={content.chip}
            onChange={(v) =>
              onChange({
                ...cmd,
                content: { ...content, chip: v ?? 0 },
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-6 col-md-2 mt-2">
          <span className="generalStyles__inputLabel">Line</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            value={content.line}
            onChange={(v) =>
              onChange({
                ...cmd,
                content: { ...content, line: v ?? 0 },
              })
            }
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
      <div className="row">
        {content.kind === "SetValueMessage" && (
          <>
            <div className="col-6 col-md-4 mt-2">
              <span className="generalStyles__inputLabel">Direction</span>
              <Select
                className="generalStyles__width100"
                size="small"
                value={content.direction}
                onChange={(val: number) =>
                  onChange({
                    ...cmd,
                    content: {
                      ...content,
                      direction: val,
                    },
                  })
                }
                style={{ marginTop: 4 }}
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="col-6 col-md-2 mt-2">
              <span className="generalStyles__inputLabel">Value</span>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={content.value}
                  onChange={(checked) =>
                    onChange({
                      ...cmd,
                      content: {
                        ...content,
                        value: checked,
                      },
                    })
                  }
                />
              </div>
            </div>
          </>
        )}
        {content.kind === "GetValueMessage" && (
          <>
            <div className="col-6 col-md-4 mt-2">
              <span className="generalStyles__inputLabel">Event Type</span>
              <Select
                className="generalStyles__width100"
                size="small"
                value={content.event_type}
                onChange={(val: number) =>
                  onChange({
                    ...cmd,
                    content: {
                      ...content,
                      event_type: val,
                    },
                  })
                }
                style={{ marginTop: 4 }}
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="col-6 col-md-2 mt-2">
              <span className="generalStyles__inputLabel">Value</span>
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={content.value}
                  onChange={(checked) =>
                    onChange({
                      ...cmd,
                      content: {
                        ...content,
                        value: checked,
                      },
                    })
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ************************************************ */
// COMPONENT ************************************** */
export interface BankContextInfo {
  bankId: string;
  bankName: string;
  lifts: { id: string; name: string }[];
}

interface CdoStm32EditorProps {
  jsonValue: string;
  onChange: (json: string) => void;
  bankContext?: BankContextInfo;
}

export default function CdoStm32Editor({
  jsonValue,
  onChange,
  bankContext = undefined,
}: CdoStm32EditorProps) {
  const [state, setState] = React.useState<Stm32CdoState>(buildDefaultState);
  const lastOutputRef = React.useRef<string>("");
  // Primitives rather than the bankContext object, so a parent that rebuilds
  // the object on every render does not re-run the load effect.
  const ctxBankId = bankContext?.bankId;
  const ctxBankName = bankContext?.bankName;

  // Parse jsonValue from parent when it changes (skip if it matches our last output)
  React.useEffect(() => {
    if (jsonValue === lastOutputRef.current) return;
    try {
      setState(normaliseState(JSON.parse(jsonValue), ctxBankId, ctxBankName));
    } catch {
      // Invalid JSON — keep current state
    }
  }, [jsonValue, ctxBankId, ctxBankName]);

  // Emit serialized JSON on every state change. The bank assignment is applied
  // here too, so the first emit carries it even when the document loaded
  // before the assignment was known.
  const emitChange = React.useCallback(
    (next: Stm32CdoState) => {
      const withBank = { ...next, bank_id: resolveBank(next.bank_id, ctxBankId, ctxBankName) };
      setState(withBank);
      const json = JSON.stringify(withBank, null, 2);
      lastOutputRef.current = json;
      onChange(json);
    },
    [onChange, ctxBankId, ctxBankName],
  );

  // ************************************************ */
  // TOP-LEVEL FIELD HANDLERS *********************** */
  const updateField = (field: string, value: unknown) => {
    emitChange({ ...state, [field]: value });
  };

  const updateBankId = (field: string, value: string) => {
    emitChange({
      ...state,
      bank_id: { ...state.bank_id, [field]: value },
    });
  };

  // ************************************************ */
  // MSG_TO_CMD HANDLERS **************************** */
  const updateMsgToCmd = (index: number, updater: (e: MsgToCmdEntry) => MsgToCmdEntry) => {
    const updated = state.msg_to_cmd.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...state, msg_to_cmd: updated });
  };

  const addMsgToCmd = () => {
    // Deliberately unconfigured: the server rejects message_content NONE, so
    // StepAdaptersCdoEditor keeps save disabled until the row names a message.
    const newEntry: MsgToCmdEntry = {
      key: buildDefaultNumatoFlatMessage(),
      value: buildDefaultGpioCommand(),
    };
    emitChange({ ...state, msg_to_cmd: [...state.msg_to_cmd, newEntry] });
  };

  const removeMsgToCmd = (index: number) => {
    emitChange({
      ...state,
      msg_to_cmd: state.msg_to_cmd.filter((_, i) => i !== index),
    });
  };

  // ************************************************ */
  // CMD_TO_LIFT_ID HANDLERS ************************ */
  const updateCmdToLift = (index: number, updater: (e: CmdToLiftEntry) => CmdToLiftEntry) => {
    const updated = state.stm_cmd_to_lift_id.map((entry, i) =>
      i === index ? updater(structuredClone(entry)) : entry,
    );
    emitChange({ ...state, stm_cmd_to_lift_id: updated });
  };

  const addCmdToLift = () => {
    const newEntry: CmdToLiftEntry = {
      key: buildDefaultGpioCommand(),
      value: { id: "", name: "" },
    };
    emitChange({
      ...state,
      stm_cmd_to_lift_id: [...state.stm_cmd_to_lift_id, newEntry],
    });
  };

  const removeCmdToLift = (index: number) => {
    emitChange({
      ...state,
      stm_cmd_to_lift_id: state.stm_cmd_to_lift_id.filter((_, i) => i !== index),
    });
  };

  // ************************************************ */
  // RENDER ***************************************** */
  return (
    <div className="container">
      {/* TOP-LEVEL FIELDS */}
      <div className="row">
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Board Name</span>
          <Input
            size="large"
            value={state.board_name}
            onChange={(e) => updateField("board_name", e.target.value)}
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Board Address</span>
          <Input
            size="large"
            value={state.board_address}
            placeholder="0x20"
            onChange={(e) => updateField("board_address", e.target.value)}
            style={{ marginTop: 4 }}
          />
        </div>
        <div className="col-12 col-md-4 mt-3">
          <span className="generalStyles__inputLabel">Device Type</span>
          <Select
            className="generalStyles__width100"
            size="large"
            value={state.dev_type}
            onChange={(val: number) => updateField("dev_type", val)}
            style={{ marginTop: 4 }}
          >
            {DEV_TYPES.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="row">
        {bankContext ? (
          <div className="col-12 col-md-6 mt-3">
            <span className="generalStyles__inputLabel">Bank</span>
            <div
              style={{
                marginTop: 4,
                padding: "4px 8px",
                background: "#1f1f1f",
                borderRadius: 4,
              }}
            >
              {bankContext.bankName || bankContext.bankId}
              <span style={{ fontSize: 10, color: "#888", display: "block" }}>
                Auto-populated from bank assignment
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Bank ID (Name)</span>
              <Input
                size="large"
                value={state.bank_id.name}
                onChange={(e) => updateBankId("name", e.target.value)}
                style={{ marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Bank ID (UUID)</span>
              <Input
                size="large"
                value={state.bank_id.id}
                placeholder="UUID"
                onChange={(e) => updateBankId("id", e.target.value)}
                style={{ marginTop: 4 }}
              />
            </div>
          </>
        )}
      </div>

      {/* ============================================= */}
      {/* MESSAGE TO COMMAND MAPPINGS */}
      <Divider orientation="left" plain>
        Message to Command Mappings ({state.msg_to_cmd.length})
      </Divider>
      {state.msg_to_cmd.map((entry, idx) => (
        <div key={`mtc_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeMsgToCmd(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* LN2 Message */}
          <span
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "#888",
            }}
          >
            LN2 Message
          </span>
          <NumatoFlatMessageFields
            msg={entry.key}
            onChange={(updatedMsg) => updateMsgToCmd(idx, (e) => ({ ...e, key: updatedMsg }))}
          />

          {/* GPIO Command */}
          <div style={{ marginTop: 12 }}>
            <span
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "#888",
              }}
            >
              GPIO Command
            </span>
            {renderGpioCommandFields(entry.value, (updatedCmd) =>
              updateMsgToCmd(idx, (e) => ({
                ...e,
                value: updatedCmd,
              })),
            )}
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addMsgToCmd} block>
        + Add Message to Command Mapping
      </Button>

      {/* ============================================= */}
      {/* COMMAND TO LIFT ID MAPPINGS */}
      <Divider orientation="left" plain>
        Command to Lift ID Mappings ({state.stm_cmd_to_lift_id.length})
      </Divider>
      {state.stm_cmd_to_lift_id.map((entry, idx) => (
        <div key={`ctl_${idx}`} style={entryStyle}>
          <Button
            type="text"
            danger
            size="small"
            style={deleteButtonStyle}
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => removeCmdToLift(idx)}
          />
          <strong style={{ display: "block", marginBottom: 8 }}>Mapping #{idx + 1}</strong>

          {/* GPIO Command */}
          <span
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "#888",
            }}
          >
            GPIO Command
          </span>
          {renderGpioCommandFields(entry.key, (updatedCmd) =>
            updateCmdToLift(idx, (e) => ({
              ...e,
              key: updatedCmd,
            })),
          )}

          {/* Lift ID */}
          <div style={{ marginTop: 12 }}>
            <span
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "#888",
              }}
            >
              Lift ID
            </span>
            <div className="row">
              {bankContext && bankContext.lifts.length > 0 ? (
                <div className="col-12 col-md-6 mt-2">
                  <span className="generalStyles__inputLabel">Lift</span>
                  <Select
                    className="generalStyles__width100"
                    size="small"
                    value={entry.value.id || undefined}
                    placeholder="Select lift"
                    onChange={(val: string) => {
                      const lift = bankContext.lifts.find((l) => l.id === val);
                      updateCmdToLift(idx, (ent) => ({
                        ...ent,
                        value: {
                          id: val,
                          name: lift?.name || "",
                        },
                      }));
                    }}
                    allowClear
                    style={{ marginTop: 4 }}
                  >
                    {bankContext.lifts.map((lift) => (
                      <Select.Option key={lift.id} value={lift.id}>
                        {lift.name || lift.id}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              ) : (
                <>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Lift Name</span>
                    <Input
                      size="small"
                      value={entry.value.name}
                      onChange={(e) =>
                        updateCmdToLift(idx, (ent) => ({
                          ...ent,
                          value: {
                            ...ent.value,
                            name: e.target.value,
                          },
                        }))
                      }
                      style={{ marginTop: 4 }}
                    />
                  </div>
                  <div className="col-12 col-md-6 mt-2">
                    <span className="generalStyles__inputLabel">Lift ID (UUID)</span>
                    <Input
                      size="small"
                      value={entry.value.id}
                      placeholder="UUID"
                      onChange={(e) =>
                        updateCmdToLift(idx, (ent) => ({
                          ...ent,
                          value: {
                            ...ent.value,
                            id: e.target.value,
                          },
                        }))
                      }
                      style={{ marginTop: 4 }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addCmdToLift} block>
        + Add Command to Lift ID Mapping
      </Button>
    </div>
  );
}
